//pages/
import { useEffect, useMemo, useRef, useState } from "react";
// SEO integration: import SEO component for structured data and meta tags.
import SEO from '@/components/SEO';
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "wouter";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Check, Shield, Clock, Infinity, Star, CreditCard, Package, Coins, Activity } from "lucide-react";
import { useCurrentPackage, usePaymentMethods, usePaymentPackages, fetchTransactionStatus, PAYMENTS_KEYS, useCreateCheckoutSession } from "@/hooks/usePayments";
import { closePaddleCheckout, openPaddleCheckout } from '@/lib/paddle';
import { publishCheckoutResult } from '@/lib/paymentWait';
// Removed embedded CheckoutPaymentPane per request
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { ShimmerBlock } from "@/components/ui/shimmer";
import { useIsMobile } from "@/hooks/use-mobile";

type Plan = {
  name: string;
  price: string; // display price with $
  credits: number; // number of analysis outputs (tokens)
  savings?: string; // discount label
  highlight: boolean;
  features: string[];
  badge?: string;
  badgeColor?: string;
  unit?: string; // per analysis output price e.g. $3.33 / analysis output
  ctaLabel?: string; // override CTA text
  headlineTokensText?: string;
  headlineReportText?: string;
};

type InlineCheckoutState = {
  planName: string;
  amountLabel: string;
  credits: number;
  request: Parameters<typeof openPaddleCheckout>[0];
};

const INLINE_CHECKOUT_TARGET = 'pricing-inline-checkout-frame';

const isTrialLike = (name: unknown) => /\btrial\b/i.test(String(name || ''));

const getInlineCheckoutFrameHeight = (isMobile: boolean) => {
  if (typeof window === 'undefined') return isMobile ? '540' : '620';

  const viewportHeight = window.innerHeight;
  const reservedSpace = isMobile ? 190 : 250;
  const minHeight = isMobile ? 420 : 560;
  const maxHeight = isMobile ? 620 : 760;

  return String(Math.max(minHeight, Math.min(viewportHeight - reservedSpace, maxHeight)));
};

const FREE_TEST_PLAN: Plan = {
  name: "FREE Test",
  price: "$0.00",
  credits: 1,
  highlight: false,
  headlineTokensText: "1 AI Data Token (Standard Report)",
  headlineReportText: "1 AI-Driven Market Insight Report",
  unit: "$0.00 / per report",
  features: [
    "Real-time Data Computation",
    "Algorithmic Risk Parameters Included",
    "Free token is non-refundable in case of NO ACTION result",
    "Intended for first-time testing only",
  ],
  ctaLabel: "Start for Free",
};

export default function Pricing() {
  const { isAuthenticated, user } = useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  // Guarded toast to avoid noisy messages while Paddle overlay opens
  const safeToast = (opts: { title: string; description?: string; variant?: any }) => {
    if (openingNewCard) return; // suppress popups during Paddle UI load
    toast(opts as any);
  };
  
  const [selectedPkg, setSelectedPkg] = useState<any>(null);
  // Embedded payment pane removed; using Paddle inline checkout directly from plan selection
  const createCheckoutSession = useCreateCheckoutSession();
  const [banner, setBanner] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [openingNewCard, setOpeningNewCard] = useState(false);
  const [inlineCheckout, setInlineCheckout] = useState<InlineCheckoutState | null>(null);
  const inlineCheckoutOpenedRef = useRef(false);
  const paymentHandledRef = useRef(false);

  // Removed unused setter to satisfy TS6133 ('setCreatingSession' unused)
  // No need to store paymentId in state for UI; we poll immediately and clear storage on completion
  const setProcessingPaymentId = (_: string | number | null) => {};
  // processingPlan removed with hosted checkout removal

  // Backend-driven data
  const packagesQuery = usePaymentPackages();
  const currentPkg = useCurrentPackage();
  const methodsQuery = usePaymentMethods();
  // Direct charge path removed; rely on hosted checkout
  // We will redirect to Hosted Checkout immediately on plan click
  // Polling helper: poll transaction status until SUCCESS/FAILED or timeout
  // Centralized refresh routine after successful payment
  const refreshAfterPayment = () => {
    // Aggressive: clear caches and refetch immediately so UI updates without navigation
    queryClient.resetQueries({ queryKey: PAYMENTS_KEYS.current });
    queryClient.resetQueries({ queryKey: PAYMENTS_KEYS.history });
    queryClient.resetQueries({ queryKey: PAYMENTS_KEYS.methods });
    queryClient.resetQueries({ queryKey: ["/api/credits/balance"] });
    queryClient.resetQueries({ queryKey: ["/api/credits/summary"] });
    queryClient.resetQueries({ queryKey: ["/api/user/profile"] });

    queryClient.invalidateQueries({ queryKey: PAYMENTS_KEYS.current });
    queryClient.invalidateQueries({ queryKey: PAYMENTS_KEYS.history });
    queryClient.invalidateQueries({ queryKey: PAYMENTS_KEYS.methods });
    queryClient.invalidateQueries({ queryKey: ["/api/credits/balance"] });
    queryClient.invalidateQueries({ queryKey: ["/api/credits/summary"] });
    queryClient.invalidateQueries({ queryKey: ["/api/user/profile"] });

    queryClient.refetchQueries({ queryKey: PAYMENTS_KEYS.methods, type: 'active' });
    queryClient.refetchQueries({ queryKey: PAYMENTS_KEYS.current, type: 'active' });
    queryClient.refetchQueries({ queryKey: ["/api/credits/balance"], type: 'active' });
    queryClient.refetchQueries({ queryKey: ["/api/user/profile"], type: 'active' });
  };

  const closeInlineCheckout = async () => {
    inlineCheckoutOpenedRef.current = false;
    setInlineCheckout(null);
    await closePaddleCheckout();
  };

  const launchInlineCheckout = (state: InlineCheckoutState) => {
    paymentHandledRef.current = false;
    inlineCheckoutOpenedRef.current = false;
    setInlineCheckout(state);
  };

  useEffect(() => {
    if (!inlineCheckout || inlineCheckoutOpenedRef.current) return;
    inlineCheckoutOpenedRef.current = true;

    const frameInitialHeight = getInlineCheckoutFrameHeight(isMobile);
    const frameStyle = isMobile
      ? 'width: 100%; min-width: 286px; height: 100%; background-color: transparent; border: none;'
      : 'width: 100%; min-width: 312px; height: 100%; background-color: transparent; border: none;';

    openPaddleCheckout({
      ...inlineCheckout.request,
      displayMode: 'inline',
      frameTarget: INLINE_CHECKOUT_TARGET,
      frameInitialHeight,
      frameStyle,
    }).catch((error) => {
      inlineCheckout.request.onError?.(error);
    });
  }, [inlineCheckout, isMobile]);

  useEffect(() => {
    const onPaddleEvent = (event: Event) => {
      const detail = (event as CustomEvent<{ name: string; data: any }>).detail;
      const eventName = String(detail?.name || '');
      const eventData = detail?.data;

      if (!inlineCheckout) return;

      if (eventName === 'checkout.completed') {
        if (paymentHandledRef.current) return;
        paymentHandledRef.current = true;

        const paymentId = eventData?.data?.custom_data?.payment_id
          || eventData?.data?.transaction_id
          || eventData?.data?.transactionId
          || localStorage.getItem('ts_last_payment_id');

        void closeInlineCheckout().then(() => {
          if (paymentId) {
            try { localStorage.setItem('ts_last_payment_id', String(paymentId)); } catch {}
            setLocation(`/payments/confirming?payment_id=${encodeURIComponent(String(paymentId))}`);
            return;
          }

          setBanner({ kind: 'error', text: 'Payment was submitted but payment_id is missing.' });
        });
      }

      if (eventName === 'checkout.payment.failed' || eventName === 'checkout.error') {
        if (paymentHandledRef.current) return;
        paymentHandledRef.current = true;

        const message = eventData?.data?.message
          || eventData?.data?.error?.message
          || 'Payment failed. Please try again.';

        void closeInlineCheckout().then(() => {
          const paymentId = localStorage.getItem('ts_last_payment_id');
          const search = new URLSearchParams({ status: 'failed', message: String(message) });
          if (paymentId) search.set('payment_id', paymentId);
          setLocation(`/payments/confirming?${search.toString()}`);
        });
      }
    };

    window.addEventListener('paddle-checkout-event', onPaddleEvent as EventListener);
    return () => window.removeEventListener('paddle-checkout-event', onPaddleEvent as EventListener);
  }, [inlineCheckout]);

  const pollTransaction = async (paymentId: string | number) => {
    let attempts = 0;
    const maxAttempts = 30; // ~60-90s with backoff
    const baseDelay = 2000;
    while (attempts < maxAttempts) {
      try {
        const status = await fetchTransactionStatus(paymentId);
        if (status?.status === 'SUCCESS') {
          setBanner({ kind: 'success', text: 'Payment completed. Updating tokens…' });
          setProcessingPaymentId(null);
          try { localStorage.removeItem('ts_last_payment_id'); } catch {}
          // Refresh credits, history, and profile so header updates instantly
          refreshAfterPayment();
          return;
        }
        if (status?.status === 'FAILED') {
          setBanner({ kind: 'error', text: 'Payment failed. Please try another card or hosted checkout.' });
          setProcessingPaymentId(null);
          try { localStorage.removeItem('ts_last_payment_id'); } catch {}
          return;
        }
        // pending- continue
      } catch (e) {
        // Ignore transient errors and continue a couple more times
      }
      attempts++;
      const delay = baseDelay + Math.min(5000, attempts * 200); // small backoff
      await new Promise(r => setTimeout(r, delay));
    }
    setBanner({ kind: 'error', text: 'Confirmation is delayed. Please check your history shortly.' });
    setProcessingPaymentId(null);
    try { localStorage.removeItem('ts_last_payment_id'); } catch {}
  };

  // Lightweight polling while on Pricing page to keep data fresh
  useEffect(() => {
    let intervalId: number | null = null;
    const POLL_MS = 5000; // 5s

    const tick = () => {
      // Only poll when tab is visible to avoid wasted requests
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      queryClient.refetchQueries({ queryKey: PAYMENTS_KEYS.current, type: 'active' });
      queryClient.refetchQueries({ queryKey: ["/api/credits/balance"], type: 'active' });
      queryClient.refetchQueries({ queryKey: ["/api/user/profile"], type: 'active' });
    };

    // Start polling when component mounts
    intervalId = (setInterval(tick, POLL_MS) as unknown as number);

    // Also refetch on focus/visibility regain for snappy updates
    const onFocus = () => tick();
    const onVisible = () => tick();
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onFocus);
      document.addEventListener('visibilitychange', onVisible);
    }

    return () => {
      if (intervalId) clearInterval(intervalId as unknown as number);
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onFocus);
      }
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  // Default method no longer used; selection happens in the picker

  // Map API packages into UI plans
  const apiPlans: Plan[] = useMemo(() => {
    const list = packagesQuery.data || [];
    // Debug raw packages when enabled
    if ((import.meta as any).env?.VITE_PADDLE_DEBUG) {
      try { console.debug('[Pricing] Raw packages', list); } catch {}
    }
    const filtered = list
      .filter(p => p.is_active !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

    const paidPkgs = filtered
      .map(p => ({ pkg: p, credits: Number(p.credits || 0), amount: Number(p.amount || 0) }))
      .filter(x => x.credits > 0 && Number.isFinite(x.amount) && x.amount > 0);

    const trialPkg = paidPkgs.find(x => isTrialLike((x.pkg as any)?.name) || isTrialLike((x.pkg as any)?.package_name) || isTrialLike((x.pkg as any)?.package_code));
    const fallbackBase = paidPkgs.slice().sort((a, b) => (b.amount / b.credits) - (a.amount / a.credits))[0];
    const base = trialPkg || fallbackBase;
    const baseUnit = base ? (base.amount / base.credits) : null;

    const maxCredits = filtered.reduce((m, p) => Math.max(m, Number(p.credits || 0)), 0);
    return filtered.map(p => {
      const isHighlight = Number(p.credits || 0) === maxCredits || /ultimate|pro/i.test(p.name || '');
      const qty = Number(p.credits || 0);

      const amount = Number(p.amount || 0);
      const planUnit = qty > 0 ? (amount / qty) : null;
      const isTrial = isTrialLike(p.name) || isTrialLike((p as any)?.package_code);
      const savings = (!isTrial && baseUnit != null && planUnit != null && Number.isFinite(planUnit) && planUnit < baseUnit)
        ? (() => {
            const comparative = baseUnit * qty;
            const save = +(comparative - amount).toFixed(2);
            if (!(save > 0)) return undefined;
            return `Save $${save.toFixed(2)}`;
          })()
        : undefined;

      return {
        name: p.name,
        price: `$${Number(p.amount).toFixed(2)}`,
        credits: p.credits,
        savings,
        highlight: !!isHighlight,
        badge: isHighlight ? 'Best Value' : undefined,
        badgeColor: isHighlight ? 'bg-[#4C73FF1A] text-[#4C73FF] border border-[#4C73FF40]' : undefined,
        unit: qty ? `$${(amount / qty).toFixed(2)} / per report` : undefined,
        // Pricing card feature bullets (no payment provider mentions)
        features: [
          `${qty} Premium Analysis ${qty === 1 ? 'Token' : 'Tokens'}`,
          `${qty} In-depth AI Research ${qty === 1 ? 'Report' : 'Reports'}`,
          `Advanced Market Data Insights`,
          `Risk Management Parameters included`,
        ],
      } as Plan;
    });
  }, [packagesQuery.data]);

  // Show FREE Test for unauthenticated users; during loading, render shimmer skeletons
  const plansLoading = packagesQuery.isLoading;
  const plansToShow: Plan[] = !plansLoading
    ? (
        isAuthenticated
          ? apiPlans.filter(p => p.name !== FREE_TEST_PLAN.name)
          : [FREE_TEST_PLAN, ...apiPlans.filter(p => p.name !== FREE_TEST_PLAN.name)]
      )
    : [];
  const gridCols = isAuthenticated ? 'lg:grid-cols-4' : 'lg:grid-cols-5';

  // Paddle first checkout (hosted overlay) flow
  const startFirstCheckout = async (pkg: any) => {
    if (!user?.id) { safeToast({ title: 'User not loaded', description: 'Login required', variant: 'destructive' }); return; }
    if (!pkg?.code) {
      safeToast({ title: 'Checkout unavailable', description: 'Package missing code', variant: 'destructive' });
      return;
    }
    try {
      // Show progress until Paddle overlay actually opens and fires callbacks
      setOpeningNewCard(true);
      const session = await createCheckoutSession.mutateAsync({
        package_code: pkg.code,
      } as any);
      const priceId = session?.paddle_price_id || pkg?.paddle_price_id || pkg?.paddlePriceId;

      if (!priceId) {
        console.error('[Pricing] Session missing paddle price id:', { pkg, session });
        safeToast({ title: 'Checkout unavailable', description: 'Paddle price is missing from checkout session', variant: 'destructive' });
        setOpeningNewCard(false);
        return;
      }

      if (session?.payment_id != null) {
        try { localStorage.setItem('ts_last_payment_id', String(session.payment_id)); } catch {}
      }
      
      if ((import.meta as any).env?.VITE_PADDLE_DEBUG) {
        console.log('[Pricing] Opening Paddle checkout:', {
          packageName: pkg.name,
          priceId,
          userId: user.id,
          paymentId: session?.payment_id,
          customerId: session?.customer_id,
        });
      }

      safeToast({ title: 'Opening payment method…', description: pkg.name });
      launchInlineCheckout({
        planName: pkg.name,
        amountLabel: `$${Number(pkg.amount || 0).toFixed(2)}`,
        credits: Number(pkg.credits || 0),
        request: {
          priceId,
          quantity: session?.quantity || 1,
          customerAuthToken: session?.customer_auth_token,
          customerId: session?.customer_id,
          customData: session?.custom_data,
          variant: session?.checkout_variant || 'one-page',
          userId: user.id,
          email: session?.prefill_email || user.email,
          autoWaitPaymentId: session?.payment_id,
          onSuccess: async (data) => {
            if ((import.meta as any).env?.VITE_PADDLE_DEBUG) {
              console.log('[Pricing] Paddle checkout success:', data);
            }
            setOpeningNewCard(false);
            const paymentId = session?.payment_id || data?.transactionId || data?.transaction?.id || data?.id;
            if (paymentId) {
              if ((import.meta as any).env?.VITE_PADDLE_DEBUG) {
                console.log('[Pricing] Payment tracking ID:', paymentId);
              }
              try { localStorage.setItem('ts_last_payment_id', String(paymentId)); } catch {}
            }
          },
          onError: async (e) => {
            const msg = (e && (e.message || e.error || e.code)) ? String(e.message || e.error || e.code) : 'Payment not started';
            await closeInlineCheckout();
            console.error('[Pricing] Paddle checkout error:', e);
            publishCheckoutResult({
              paymentId: session?.payment_id || 'unknown',
              status: 'FAILED',
              message: msg,
            });
            safeToast({ title: 'Checkout error', description: msg, variant: 'destructive' });
            setOpeningNewCard(false);
          },
          onClose: async () => {
            if ((import.meta as any).env?.VITE_PADDLE_DEBUG) {
              console.log('[Pricing] Paddle checkout closed');
            }
            await closeInlineCheckout();
            setOpeningNewCard(false);
            if (paymentHandledRef.current) return;
            setBanner({ kind: 'error', text: 'Checkout was closed before payment completed.' });
          },
        },
      });
      setOpeningNewCard(false);
    } catch (e:any) {
      console.error('[Pricing] Failed to open Paddle checkout:', e);
      safeToast({ title: 'Failed to open checkout', description: String(e?.message || 'Unknown error'), variant: 'destructive' });
      setOpeningNewCard(false);
    }
  };

  const handlePlanSelect = async (planName: string) => {
    if (!isAuthenticated) { setLocation('/login'); return; }
    const pkg = (packagesQuery.data || []).find(p => p.name === planName);
    if (!pkg) { safeToast({ title: 'Package not found', description: 'Refresh and retry', variant: 'destructive' }); return; }
    setSelectedPkg(pkg);
    const priceId = pkg?.paddle_price_id || (pkg as any)?.paddlePriceId;
    if (!priceId) {
      safeToast({ title: 'Checkout unavailable', description: 'Price not configured for this plan', variant: 'destructive' });
      try { console.debug('Selected package without priceId', pkg); } catch {}
      return;
    }
    startFirstCheckout(pkg);
  };

  // Hosted checkout removed in favor of embedded pane for new card flow

  // Direct charge of saved token is currently disabled; hosted checkout will surface saved methods.

  // Parse redirect result from hosted checkout
  const search = typeof window !== 'undefined' ? window.location.search : '';
  useMemo(() => {
    if (!search) return;
    const params = new URLSearchParams(search);
    const s = params.get('payment');
    const pid = params.get('pid');
    // Show inline status badge using recent confirmation marker
    try {
      const markRaw = localStorage.getItem('ts_payment_recent_confirmed');
      if (markRaw) {
        const mark = JSON.parse(markRaw);
        const age = Date.now() - Number(mark?.t || 0);
        if (Number.isFinite(age) && age < 60_000) {
          setBanner({ kind: 'success', text: `Payment confirmed. +${mark?.delta ?? ''} tokens added` });
        }
        localStorage.removeItem('ts_payment_recent_confirmed');
      }
    } catch {}
    if (s === 'success') {
      setBanner({ kind: 'success', text: 'Processing payment… do not close the page.' });
      let effectivePid = pid;
      if (!effectivePid) {
        try { effectivePid = localStorage.getItem('ts_last_payment_id') || undefined as any; } catch {}
      }
      if (effectivePid) {
        setProcessingPaymentId(effectivePid);
        pollTransaction(effectivePid);
      }
    }
    if (s === 'failure') setBanner({ kind: 'error', text: 'Payment failed. Please try again.' });
    // Cleanup query from URL without reloading
    if (s) {
      try { window.history.replaceState({}, '', window.location.pathname); } catch {}
    }
  }, [search]);

  // Auto-dismiss banner after a few seconds
  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => setBanner(null), 4500);
    return () => clearTimeout(t);
  }, [banner]);

  // Instant/hosted flows removed in favor of embedded tokenization UI

  // Token-based direct charge is no longer used; we add and then charge a saved method

  // Instant pay flow removed for now

  // Hosted checkout is triggered directly by handlePlanSelect now

  // Build dynamic OfferCatalog / Offers JSON-LD for pricing page (rich results potential).
  const offerJson = plansToShow.length ? {
    '@context': 'https://schema.org',
    '@type': 'OfferCatalog',
    'name': 'AI Market Research Token Packages',
    'url': 'https://tradesagepro.com/pricing',
    'itemListElement': plansToShow.map(p => ({
      '@type': 'Offer',
      'name': p.name,
      'price': p.price.replace(/[$]/,'') ,
      'priceCurrency': 'USD',
      'availability': 'https://schema.org/InStock'
    }))
  } : null;

  return (
    <Layout>
      {/* SEO TAGS: Pricing page focuses on credit-based AI market research credit packages; OfferCatalog used for structured data. */}
      <SEO
        title="Pricing for AI Market Research & Analysis | Flexible Token Packs"
        description="Choose flexible token packs for AI-powered market analysis insights. No subscriptions—buy tokens and run data-driven analysis anytime."
        canonical="https://tradesagepro.com/pricing"
        type="product"
        jsonLd={offerJson || undefined}
      />
      {/* END SEO TAGS */}
      <div className="min-h-screen bg-[#10151D] text-[hsl(210,9%,91%)] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div aria-live="polite" role="status">
            {banner && (
              <div className={`mb-6 text-sm rounded p-3 border ${banner.kind==='success' ? 'bg-emerald-900/10 border-emerald-800/50 text-emerald-300' : 'bg-red-900/10 border-red-800/50 text-red-300'}`}>
                {banner.text}
              </div>
            )}
          </div>
          {/* Header */}
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-3">
              <Activity className="w-5 h-5 text-[#4C73FF] opacity-80" />
              <svg width="80" height="22" viewBox="0 0 80 22" className="opacity-80">
                <defs>
                  <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#4C73FF"/>
                    <stop offset="100%" stopColor="#00FF8C"/>
                  </linearGradient>
                </defs>
                <polyline fill="none" stroke="url(#grad)" strokeWidth="2" points="0,18 10,12 20,14 30,8 40,12 50,6 60,10 70,4 80,7" className="animate-pulse" />
              </svg>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 font-sans tracking-tight">
              Flexible Pricing for <span className="text-[#4C73FF] drop-shadow-[0_0_18px_rgba(76,115,255,0.45)]">AI Market Analysis Outputs</span>
            </h1>
            <p className="text-lg sm:text-xl text-[hsl(210,9%,91%)] max-w-3xl mx-auto leading-relaxed">
              <span className="font-semibold">Flexible token-based pricing with no subscriptions.</span>
              <span className="opacity-90"> Pay only for the analyses you run. All tokens never expire and include full access to our AI-powered research platform.</span>
            </p>
          </div>

          {/* Summary is shown only to authenticated users */}
          {isAuthenticated && (
            <Card className="mt-8 mb-12 rounded-2xl border border-[#182134] bg-gradient-to-r from-[#0F1623] to-[#0C121C] shadow-[0_0_40px_rgba(12,18,28,0.6)]">
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                  {/* Active Pack */}
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-[#1C2A44] bg-[#0D1420]">
                    <div className="w-9 h-9 rounded bg-[#4C73FF20] flex items-center justify-center"><Package className="w-5 h-5 text-[#4C73FF]" /></div>
                    <div className="text-sm text-gray-300">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400">Active Pack</div>
                      {currentPkg.isLoading ? (
                        <div className="h-4 w-32 bg-gray-700/60 rounded animate-pulse" />
                      ) : (
                        <div className="text-white font-semibold inline-flex items-center gap-2">
                          <span className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border border-[#00FF8C] bg-white/5">
                            <span className="w-2 h-2 rounded-full bg-[#00FF8C] animate-pulse" />
                            {currentPkg.data?.package_name || currentPkg.data?.package_code || 'Trial Pack'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Tokens */}
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-[#1C2A44] bg-[#0D1420]">
                    <div className="w-9 h-9 rounded bg-[#00FF8C20] flex items-center justify-center"><Coins className="w-5 h-5 text-[#00FF8C]" /></div>
                    <div className="text-sm text-gray-300">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400">Tokens</div>
                      {currentPkg.isLoading ? (
                        <div className="h-4 w-16 bg-gray-700/60 rounded animate-pulse" />
                      ) : (
                        <div className="text-[#00FF8C] text-2xl font-bold leading-none">{typeof currentPkg.data?.credits_balance === 'number' ? currentPkg.data?.credits_balance : '—'}</div>
                      )}
                    </div>
                  </div>
                  {/* Cards */}
                  <div className="flex items-center gap-3 p-3 rounded-xl border border-[#1C2A44] bg-[#0D1420]">
                    <div className="w-9 h-9 rounded bg-[#4C73FF20] flex items-center justify-center"><CreditCard className="w-5 h-5 text-[#4C73FF]" /></div>
                    <div className="text-sm text-gray-300">
                      <div className="text-[11px] uppercase tracking-wide text-gray-400">Cards</div>
                      {methodsQuery.isLoading ? (
                        <div className="h-4 w-24 bg-gray-700/60 rounded animate-pulse" />
                      ) : (methodsQuery.data?.length ?? 0) === 0 ? (
                        <div className="text-white/80">No saved cards</div>
                      ) : (() => {
                        const methods = methodsQuery.data || [];
                        const def = methods.find(m => m.is_default) || methods[0];
                        const exp = def?.exp_month && def?.exp_year
                          ? `${String(def.exp_month).padStart(2,'0')}/${String(def.exp_year).slice(-2)}`
                          : null;
                        return (
                          <div>
                            <div className="text-white font-semibold">{def?.scheme || 'Card'} •••• {def?.last4 || '—'}</div>
                            <div className="text-[10px] text-gray-400">{exp || '—'}</div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                  {/* Manage */}
                  <div className="flex items-center justify-end">
                    <Button variant="ghost" className="text-xs text-gray-300 hover:text-white" onClick={() => setLocation('/profile')}>Manage</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Inline payment result removed; handled inside payment pane */}

          {/* Shimmer skeletons while fetching pricing */}
          {plansLoading && (
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-6 mb-16`} aria-hidden>
              {Array.from({ length: isAuthenticated ? 4 : 5 }).map((_, i) => (
                <Card key={i} className="rounded-2xl bg-[hsl(215,15%,14%)] border border-[#1C2A44] overflow-hidden">
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <ShimmerBlock className="h-6 w-40" />
                      <div className="flex items-end gap-2">
                        <ShimmerBlock className="h-8 w-20" />
                        <ShimmerBlock className="h-4 w-10" />
                      </div>
                      <ShimmerBlock className="h-4 w-32" />
                      <div className="grid grid-cols-2 gap-2">
                        <ShimmerBlock className="h-7" />
                        <ShimmerBlock className="h-7" />
                        <ShimmerBlock className="h-7" />
                        <ShimmerBlock className="h-7" />
                      </div>
                      <ShimmerBlock className="h-10 w-full" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Pricing Cards */}
          <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-6 mb-16`}>
            {!plansLoading && plansToShow.map((plan, index) => (
              <Card
                key={plan.name}
                className={`rounded-2xl bg-[hsl(215,15%,14%)] ${
                  plan.highlight
                    ? 'border-2 border-[#4C73FF] shadow-[0_0_30px_rgba(76,115,255,0.25)]'
                    : 'border border-[#1C2A44] hover:border-[#4C73FF]'
                } transition-all duration-300 relative animate-slide-in`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {(currentPkg.data?.package_name && currentPkg.data?.package_name === plan.name) && (
                  <div className="absolute top-3 right-3 text-[10px] px-2 py-0.5 rounded bg-[#052E1A] text-[#00FF8C] border border-[#00FF8C40]">Your current plan</div>
                )}
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className={`px-3 py-1.5 text-xs font-semibold rounded-full bg-gradient-to-r from-[#4C73FF55] to-[#00FF8C55] text-white border border-[#4C73FF40]`}>
                      {plan.badge}
                    </Badge>
                  </div>
                )}

                <CardContent className="p-6 text-center h-full flex flex-col">
                  {/* Top section: name + price */}
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold text-white mb-3">{plan.name}</h3>
                    <div className="mb-3 leading-none inline-flex items-baseline gap-1">
                      {/* Price typography refinement */}
                      {(() => {
                        const raw = plan.price || '';
                        const m = raw.match(/^(\$|€|£)?\s*([0-9]+)\.?([0-9]{2})?$/);
                        const sym = m?.[1] || '$';
                        const major = m?.[2] || raw.replace(/[^0-9]/g,'') || '0';
                        const minor = m?.[3] || '00';
                        return (
                          <>
                            <span className="text-white/80 text-2xl" style={{verticalAlign:'baseline'}}>{sym}</span>
                            <span className="text-4xl font-extrabold text-white" style={{filter:'drop-shadow(0 0 10px rgba(76,115,255,0.35))'}}>{major}</span>
                            <span className="text-white/70 text-sm" style={{verticalAlign:'super'}}>.{minor}</span>
                          </>
                        );
                      })()}
                    </div>
                    {/* Emphasize tokens and analysis outputs as separate lines */}
                    {plan.headlineTokensText ? (
                      <>
                        <div className="text-sm font-medium text-white mb-0.5">{plan.headlineTokensText}</div>
                        {plan.headlineReportText ? (
                          <div className="text-xs text-white/80 mb-1">{plan.headlineReportText}</div>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <div className="text-sm font-medium text-white mb-0.5">{plan.credits} Premium Analysis {plan.credits === 1 ? 'Token' : 'Tokens'}</div>
                        <div className="text-xs text-white/80 mb-1">{plan.credits} In-depth AI Research {plan.credits === 1 ? 'Report' : 'Reports'}</div>
                      </>
                    )}
                    {plan.unit && (
                      <div className="text-white/60 text-xs font-mono mb-2">{plan.unit}</div>
                    )}
                    {plan.savings && (
                      <div className="text-[#00FF8C] text-xs font-semibold mb-1">
                        {plan.savings}
                      </div>
                    )}
                  </div>

                  {/* Features: FREE Test uses bullets; other plans use chips */}
                  <div className="flex-1 mb-6">
                    {plan.name === FREE_TEST_PLAN.name ? (
                      <div className="text-xs text-white/90 space-y-2 text-left">
                        {plan.features.map((feature, featureIndex) => (
                          <div key={featureIndex}>• {feature}</div>
                        ))}
                      </div>
                    ) : (
                      <ul className="flex flex-wrap gap-2 text-[11px] justify-start">
                        {plan.features.map((feature, featureIndex) => (
                          <li
                            key={featureIndex}
                            className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full border border-[#1C2A44] bg-[#0D1420] text-white/90"
                          >
                            <Check className="w-3 h-3 text-[#00FF8C]" />
                            <span className="whitespace-normal break-words text-left overflow-hidden">
                              {feature}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="grid grid-cols-1 gap-2">
                    {(() => {
                      const isFreeTest = plan.name === FREE_TEST_PLAN.name;
                      if (isFreeTest) {
                        return (
                          <Button
                            className="w-full border border-[#4C73FF] text-[#4C73FF] bg-transparent hover:bg-[#4C73FF] hover:text-white transition-all duration-300"
                            onClick={() => setLocation(isAuthenticated ? '/start-trading' : '/register')}
                          >
                            {plan.ctaLabel || 'Start for Free'}
                          </Button>
                        );
                      }

                      const pkgForPlan = (packagesQuery.data || []).find(p => p.name === plan.name);
                      const priceIdForPlan = (pkgForPlan as any)?.paddle_price_id || (pkgForPlan as any)?.paddlePriceId;
                      const disabled = !priceIdForPlan; // Disable if not purchasable
                      const isOpeningThis = openingNewCard && selectedPkg?.name === plan.name;
                      const buttonEl = (
                        <Button
                          className={
                            plan.highlight
                              ? "w-full bg-[#4C73FF] text-white hover:brightness-110 transition-all duration-300"
                              : "w-full border border-[#4C73FF] text-[#4C73FF] bg-transparent hover:bg-[#4C73FF] hover:text-white transition-all duration-300"
                          }
                          onClick={() => handlePlanSelect(plan.name)}
                          disabled={disabled || isOpeningThis}
                          aria-disabled={disabled || isOpeningThis}
                          aria-busy={isOpeningThis}
                          aria-label={disabled ? 'Checkout unavailable for this package' : undefined}
                        >
                          {isOpeningThis
                            ? 'Processing…'
                            : (plan.ctaLabel
                                ? plan.ctaLabel
                                : (plan.highlight ? (<><Star className="w-4 h-4 mr-2" />Best Value</>) : 'Choose Plan'))}
                        </Button>
                      );
                      if (!disabled) return buttonEl;
                      return (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>{buttonEl}</TooltipTrigger>
                            <TooltipContent>
                              Checkout unavailable for this package
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      );
                    })()}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Checkout panel removed: direct hosted checkout on "Choose Plan" click */}

          {/* Features Comparison */}
          <Card className="bg-[hsl(215,15%,14%)] border-[#1C2A44] mb-16 rounded-2xl">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-white text-center mb-8">
                All Plans Include
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#4C73FF20] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Shield className="w-8 h-8 text-[#4C73FF]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">AI-Powered Analysis Outputs</h3>
                  <p className="text-[hsl(210,9%,91%)]">
                    Advanced AI-generated research reports with suggested data points for position initiation, risk management parameters, and target data points for position closure.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#00FF8C20] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Clock className="w-8 h-8 text-[#00FF8C]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">Real-Time Insights</h3>
                  <p className="text-[hsl(210,9%,91%)]">
                    Instant analysis insights based on current market conditions and data-driven context.
                  </p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#4C73FF20] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Infinity className="w-8 h-8 text-[#4C73FF]" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">No Expiration</h3>
                  <p className="text-[hsl(210,9%,91%)]">
                    Tokens never expire and can be used at your own pace. 
                    Full access to all platform features.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Benefits */}
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-white mb-8">Why Choose TradeSagePro?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {[
                  { icon: Shield, title: "Secure Payment", desc: "SSL encrypted transactions" },
                  { icon: Clock, title: "Instant Access", desc: "Immediate analysis insights" },
                  { icon: Infinity, title: "No Expiration", desc: "Tokens never expire" },
                  { icon: Star, title: "Model Metrics", desc: "Historical concordance & validation" }
                ].map((benefit, index) => (
                <div key={index} className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 bg-[#4C73FF20] rounded-full flex items-center justify-center mb-3">
                    <benefit.icon className="w-6 h-6 text-[#4C73FF]" />
                  </div>
                  <h3 className="text-white font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-[hsl(210,9%,91%)] text-sm">{benefit.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <Card className="bg-[hsl(215,15%,18%)] border-gray-700">
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-white text-center mb-8">
                Frequently Asked Questions
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-white font-semibold mb-2">How do tokens work?</h3>
                  <p className="text-[hsl(210,9%,91%)] text-sm mb-4">
                    Each token allows you to run one AI-powered analysis. Tokens never expire and can be used whenever you want to request an analysis insight.
                  </p>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">What's included in each analysis?</h3>
                  <p className="text-[hsl(210,9%,91%)] text-sm mb-4">
                    Every analysis includes suggested data points for initiation, risk management parameters, target closure data points, pre-defined closure strategy (RR), model validation metric, and detailed AI reasoning.
                  </p>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">Can I upgrade my plan?</h3>
                  <p className="text-[hsl(210,9%,91%)] text-sm mb-4">
                    Yes! You can purchase additional tokens at any time. All tokens are additive and never expire.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Removed bottom summary block (now shown below header) */}

          {/* CTA */}
          <div className="text-center mt-16">
            <p className="text-[hsl(210,9%,91%)] mb-6 text-lg">
              Ready to start exploring data-driven market analysis insights?
            </p>
            {!isAuthenticated && (
              <Button
                className="bg-[hsl(193,100%,50%)] text-[hsl(226,26%,7%)] px-8 py-4 text-lg font-semibold hover:scale-105 transition-all duration-300 animate-glow"
                onClick={() => setLocation('/login')}
              >
                Get Started Now
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Inline Paddle checkout dialog */}
      <Dialog
        open={!!inlineCheckout}
        onOpenChange={(open) => {
          if (!open) {
            void closeInlineCheckout();
          }
        }}
      >
        <DialogContent
          className="h-[100dvh] w-screen max-w-none overflow-hidden rounded-none border-0 bg-[#08111B]/98 p-0 text-white shadow-none backdrop-blur-xl sm:h-auto sm:max-h-[94dvh] sm:w-[96vw] sm:max-w-4xl sm:rounded-[28px] sm:border sm:border-white/10 sm:bg-[#08111B]/95 sm:shadow-[0_25px_80px_rgba(0,0,0,0.55)]"
          aria-describedby="inline-checkout-description"
        >
          <div className="flex h-full min-h-0 flex-col p-3 sm:p-5 lg:p-7">
              <DialogTitle className="sr-only">Payment Checkout</DialogTitle>
              <DialogDescription id="inline-checkout-description" className="sr-only">
                Embedded Paddle checkout.
              </DialogDescription>
              <div className="flex min-h-0 flex-1 rounded-[22px] border border-white/10 bg-[linear-gradient(180deg,#F7FAFC_0%,#EEF4F8_100%)] p-2 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] sm:rounded-[24px] sm:p-4">
                <div className={`${INLINE_CHECKOUT_TARGET} h-[calc(100dvh-170px)] max-h-[calc(100dvh-170px)] min-h-[420px] w-full overflow-hidden rounded-[18px] bg-white sm:h-[min(72dvh,760px)] sm:max-h-[72dvh] sm:min-h-[560px] sm:rounded-[20px]`} />
              </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Embedded payment pane removed; checkout opens directly from the pricing cards */}
    </Layout>
  );
}